import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import {
  Plus,
  Edit2,
  Eye,
  Download,
  Search,
  Loader2,
  X,
  Save,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertTriangle,
  Trash2,
  Truck,
  PackageMinus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import BaseApi from "../../services/base-api";
import ConfirmModal from "../../components/Modals/Confirm";
import { ShoppingCartCheckout } from "@mui/icons-material";
import LoadingComponent from "../../components/LoadingComponent";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { Tooltip } from "@mui/material";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";
import type { Product, ProductVariation } from "../../types/Product";

interface Supplier {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
}

interface PurchaseOrderItem {
  id?: string;
  itemId?: string;
  productId: string | null;
  variationId?: string | null;
  productName?: string;
  Product: Product | null;
  ProductVariation?:
    | (Partial<ProductVariation> & {
        product?: Product | null;
        productId?: string | null;
        sku?: string;
      })
    | null;
  variation?: ProductVariation;
  quantity: number;
  unitCost: number;
  unitPrice?: number;
  costPrice?: number;
  total: number;
  stockAppliedQuantity?: number;
}

interface PurchaseOrder {
  id?: string;
  orderNumber?: string;
  poNumber?: string;
  supplierId: string;
  supplier?: Supplier | null;
  items: PurchaseOrderItem[];
  expectedDeliveryDate: string;
  notes?: string;
  status: "draft" | "pending" | "approved" | "delivered" | "cancelled";
  total: number;
  createdAt?: string;
}

interface POStats {
  total: number;
  pending: number;
  approved: number;
  delivered: number;
  cancelled: number;
  overdue: number;

  totalValue: number;
  deliveredValue: number;

  byStatus: {
    status: string;
    count: number;
    total: number;
  }[];
}

interface DeliverItemsFormItem {
  itemId: string;
  productName: string;
  variationName: string;
  orderedQuantity: number;
  quantityReceived: number;
}

const PurchaseOrderManagementPage = () => {
  const { success, err } = useToast();
  const { t, i18n } = useTranslation();

  const { permissions } = useAuth();
  // Check if user has permission to create or edit products
  const canCreate = hasPermission(
    permissions,
    PERMISSIONS.CREATE_PURCHASE_ORDERS,
  );
  const canEdit = hasPermission(permissions, PERMISSIONS.EDIT_PURCHASE_ORDERS);
  // User can add category if they have either create or edit permission
  const canAddEdit = canCreate || canEdit;

  const canDelete = hasPermission(
    permissions,
    PERMISSIONS.DELETE_PURCHASE_ORDERS,
  );

  const canCreateReturn = hasPermission(
    permissions,
    PERMISSIONS.CREATE_PURCHASE_RETURNS,
  );
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());

  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy] = useState<"orderDate" | "total">("orderDate");
  const [sortOrder] = useState<"asc" | "desc">("desc");

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState<"edit" | "view">("edit");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [stats, setStats] = useState<POStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const latestRequestRef = useRef(0);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [productDetailsCache, setProductDetailsCache] = useState<
    Map<string, Product>
  >(new Map());
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [productSearchModalOpen, setProductSearchModalOpen] = useState(false);
  const [productSearchModalRowIndex, setProductSearchModalRowIndex] = useState<
    number | null
  >(null);
  const [productSearchQuery, setProductSearchQuery] = useState<string>("");

  const [formData, setFormData] = useState<PurchaseOrder>({
    supplierId: "",
    items: [],
    expectedDeliveryDate: "",
    notes: "",
    status: "draft",
    total: 0,
  });

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  const [errors, setErrors] = useState({
    supplierId: "",
    items: "",
    expectedDeliveryDate: "",
  });
  const [deliverItemsModalOpen, setDeliverItemsModalOpen] = useState(false);
  const [deliverItemsOrder, setDeliverItemsOrder] =
    useState<PurchaseOrder | null>(null);
  const [deliverItemsData, setDeliverItemsData] = useState<{
    receivedItems: DeliverItemsFormItem[];
    notes: string;
  }>({
    receivedItems: [],
    notes: "",
  });
  const [isReceivingItems, setIsReceivingItems] = useState(false);
  const [downloadingOrderId, setDownloadingOrderId] = useState<string | null>(
    null,
  );

  const fetchPurchaseOrders = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const res = await BaseApi.get(`/purchase-orders?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setPurchaseOrders(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching purchase orders:", error);
      err(t("Failed to load purchase orders"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    debouncedSearch,
    statusFilter,
    err,
    t,
  ]);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchPOStats();
  }, []);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    const total = formData.items.reduce((sum, item) => sum + item.total, 0);
    setFormData((prev) => (prev.total === total ? prev : { ...prev, total }));
  }, [formData.items]);

  // Helper function to filter products based on search query
  const getFilteredProducts = (query: string) => {
    if (!query.trim()) return products;

    const lowerQuery = query.toLowerCase();
    return products.filter((product) => {
      const productName =
        typeof product.name === "string"
          ? product.name
          : product.name?.en || product.name?.ar || "Unknown Product";

      const sku = product.sku?.toLowerCase() || "";
      const name = productName.toLowerCase();

      return name.includes(lowerQuery) || sku.includes(lowerQuery);
    });
  };

  // GET /purchase-orders/suppliers - Get list of suppliers from POs
  const fetchSuppliers = async () => {
    try {
      const res = await BaseApi.get("/users?role=supplier");
      if (res.success && res?.data) {
        setSuppliers(res.data);
      } else {
        // Fallback to users endpoint
        const usersRes = await BaseApi.get("/users");
        if (usersRes.success && usersRes?.data) {
          setSuppliers(usersRes.data);
        }
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await BaseApi.get("/products");
      if (res.success && res?.data) {
        setProducts(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  // Fetch product details including variations
  const fetchProductDetails = async (productId: string) => {
    try {
      // Check if already cached
      if (productDetailsCache.has(productId)) {
        return productDetailsCache.get(productId);
      }

      setLoadingProductId(productId);
      const res = await BaseApi.get(`/products/${productId}`);
      if (res.success && res?.data) {
        // Cache the product details
        setProductDetailsCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(productId, res.data);
          return newCache;
        });
        return res.data;
      }
    } catch (error) {
      console.error("Failed to fetch product details:", error);
      err(t("Failed to fetch product variations"));
    } finally {
      setLoadingProductId(null);
    }
    return null;
  };

  // GET /purchase-orders/stats/overview - Get purchase order statistics
  const fetchPOStats = async () => {
    try {
      const res = await BaseApi.get("/purchase-orders/stats/overview");
      if (res.success && res?.data) {
        setStats(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch PO stats:", error);
    }
  };

  // GET /purchase-orders/pending-approvals - Get pending purchase orders requiring approval
  // const fetchPendingApprovals = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await BaseApi.get("/purchase-orders/pending-approvals");
  //     if (res.success && res?.data) {
  //       setPurchaseOrders(res.data);
  //       setFilteredOrders(res.data);
  //       setStatusFilter("all");
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch pending approvals:", error);
  //     err(t("Failed to load pending approvals"));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // GET /purchase-orders/overdue - Get overdue purchase orders
  // const fetchOverdueOrders = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await BaseApi.get("/purchase-orders/overdue");
  //     if (res.success && res?.data) {
  //       setPurchaseOrders(res.data);
  //       setFilteredOrders(res.data);
  //       setStatusFilter("all");
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch overdue orders:", error);
  //     err(t("Failed to load overdue orders"));
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // GET /purchase-orders/{id} - Get purchase order by ID
  const fetchPurchaseOrderById = async (id: string) => {
    try {
      const res = await BaseApi.get(`/purchase-orders/${id}`);
      if (res.success && res?.data) {
        return res.data;
      }
    } catch (error) {
      console.error("Failed to fetch purchase order:", error);
    }
    return null;
  };

  const getLocalizedProductName = (product?: Product | null) => {
    if (!product?.name) return "";

    if (typeof product.name === "string") {
      return product.name;
    }

    return (
      product.name[i18n.language as keyof typeof product.name] ||
      product.name.en ||
      product.name.ar ||
      ""
    );
  };

  const getProductDisplayName = (item: PurchaseOrderItem) =>
    item.productName ||
    getLocalizedProductName(item.Product) ||
    getLocalizedProductName(item.ProductVariation?.product) ||
    t("Unnamed product");

  const getVariationDisplayName = (item: PurchaseOrderItem) => {
    const attributeValues = Object.values(
      item.variation?.attributes || item.ProductVariation?.attributes || {},
    ).filter(Boolean);

    if (attributeValues.length > 0) {
      return attributeValues.join(", ");
    }

    const variationSku = item.ProductVariation?.sku || item.variation?.sku;
    const productSku =
      item.ProductVariation?.product?.sku || item.Product?.sku || "";

    if (variationSku) {
      if (productSku && variationSku.startsWith(`${productSku}-`)) {
        return variationSku.slice(productSku.length + 1).replace(/-/g, " ");
      }

      return variationSku;
    }

    return t("No variations");
  };

  const getSupplierDisplayName = (order: PurchaseOrder) => {
    const supplier = order.supplier;
    if (!supplier) {
      return order.supplierId;
    }

    const fullName =
      `${supplier.firstName ?? ""} ${supplier.lastName ?? ""}`.trim();
    return (
      fullName ||
      supplier.name ||
      supplier.phone ||
      supplier.email ||
      order.supplierId
    );
  };

  const getItemPrice = (item: PurchaseOrderItem) =>
    Number(item.costPrice ?? item.unitPrice ?? item.unitCost ?? 0);

  const getPurchaseOrderItemId = (item: PurchaseOrderItem) =>
    item.itemId ||
    item.id ||
    item.variationId ||
    item.ProductVariation?.id ||
    item.productId ||
    item.Product?.id ||
    "";

  const normalizePurchaseOrderItem = (
    item: PurchaseOrderItem,
  ): PurchaseOrderItem => {
    const quantity = Number(item.quantity || 0);
    const price = getItemPrice(item);
    const nestedProduct = item.ProductVariation?.product || null;
    const resolvedProduct = item.Product || nestedProduct;

    return {
      ...item,
      productId:
        item.productId ||
        item.ProductVariation?.productId ||
        nestedProduct?.id ||
        "",
      variationId: item.variationId || item.ProductVariation?.id || null,
      productName: item.productName || getLocalizedProductName(resolvedProduct),
      Product: resolvedProduct,
      variation: (item.variation || item.ProductVariation) as
        | ProductVariation
        | undefined,
      quantity,
      unitCost: price,
      unitPrice: price,
      costPrice: price,
      total: quantity * price,
    };
  };

  const normalizePurchaseOrder = (order: PurchaseOrder): PurchaseOrder => {
    const normalizedItems = (order.items || []).map(normalizePurchaseOrderItem);

    return {
      ...order,
      items: normalizedItems,
      total: normalizedItems.reduce((sum, item) => sum + item.total, 0),
    };
  };

  const getBaseProductId = (item: PurchaseOrderItem) => {
    if (item.Product?.id) return item.Product.id;
    if (item.ProductVariation?.productId)
      return item.ProductVariation.productId;
    if (item.ProductVariation?.product?.id)
      return item.ProductVariation.product.id;

    if (
      item.productId &&
      item.variationId &&
      item.productId.endsWith(`-${item.variationId}`)
    ) {
      return item.productId.slice(0, -(item.variationId.length + 1));
    }

    return item.productId || "";
  };

  const hydratePurchaseOrderProducts = async (order: PurchaseOrder) => {
    const hydratedItems = await Promise.all(
      order.items.map(async (item) => {
        const baseProductId = getBaseProductId(item);

        if (!baseProductId) {
          return item;
        }

        const detailedProduct = await fetchProductDetails(baseProductId);
        if (!detailedProduct) {
          return item;
        }

        const selectedVariationId =
          item.variationId || item.ProductVariation?.id;
        const selectedVariation =
          detailedProduct.variations?.find(
            (variation: ProductVariation) =>
              variation.id === selectedVariationId,
          ) || item.variation;

        return normalizePurchaseOrderItem({
          ...item,
          productId: detailedProduct.id,
          Product: detailedProduct,
          ProductVariation: item.ProductVariation,
          variation: selectedVariation,
        });
      }),
    );

    return {
      ...order,
      items: hydratedItems,
      total: hydratedItems.reduce((sum, item) => sum + item.total, 0),
    };
  };

  const updateProductRow = (index: number, field: string, value: string) => {
    const updatedItems = [...formData.items];
    const item = updatedItems[index];

    if (field === "productId") {
      const selectedProduct = products.find((p) => p.id === value);
      if (selectedProduct) {
        item.productId = value;
        const productName =
          typeof selectedProduct.name === "string"
            ? selectedProduct.name
            : selectedProduct.name?.en ||
              selectedProduct.name?.ar ||
              "Unknown Product";
        item.productName = productName;
        item.Product = selectedProduct;
        item.ProductVariation = null;
        item.unitCost = selectedProduct.price;
        item.unitPrice = selectedProduct.price;
        item.costPrice = selectedProduct.price;
        item.total = item.quantity * selectedProduct.price;
        // Reset variation when product changes
        item.variation = undefined;

        // Fetch product details to get variations
        fetchProductDetails(value).then((detailedProduct) => {
          if (detailedProduct) {
            setFormData((prev) => {
              const updatedFormItems = [...prev.items];
              updatedFormItems[index].Product = detailedProduct;
              return { ...prev, items: updatedFormItems };
            });
          }
        });
      } else {
        // Reset when no product selected
        item.productId = "";
        item.productName = "";
        item.Product = null;
        item.ProductVariation = null;
        item.unitCost = 0;
        item.unitPrice = 0;
        item.costPrice = 0;
        item.total = 0;
        item.variation = undefined;
      }
    } else if (field === "variationId") {
      const selectedProduct = item.Product;
      if (selectedProduct && selectedProduct.variations) {
        const selectedVariation = selectedProduct.variations.find(
          (v) => v.id === value,
        );
        if (selectedVariation) {
          const variationPrice =
            selectedVariation.price ?? selectedProduct.price;

          item.variation = selectedVariation;
          item.unitCost = variationPrice;
          item.unitPrice = variationPrice;
          item.costPrice = variationPrice;
          item.total = item.quantity * variationPrice;
          // Update productId to include variation
          item.productId = `${selectedProduct.id}-${selectedVariation.id}`;
          // Update product name to include variation
          const baseName =
            typeof selectedProduct.name === "string"
              ? selectedProduct.name
              : selectedProduct.name?.en ||
                selectedProduct.name?.ar ||
                "Unknown Product";
          item.productName = baseName;
        } else {
          // Reset variation
          item.variation = undefined;
          item.productId = selectedProduct.id;
          item.productName =
            typeof selectedProduct.name === "string"
              ? selectedProduct.name
              : selectedProduct.name?.en ||
                selectedProduct.name?.ar ||
                "Unknown Product";
          item.unitCost = selectedProduct.price;
          item.unitPrice = selectedProduct.price;
          item.costPrice = selectedProduct.price;
          item.total = item.quantity * selectedProduct.price;
        }
      }
    } else if (field === "quantity") {
      item.quantity = parseInt(value) || 0;
      item.total = item.quantity * getItemPrice(item);
    } else if (field === "costPrice") {
      const price = parseFloat(value) || 0;
      item.costPrice = price;
      item.unitPrice = price;
      item.unitCost = price;
      item.total = item.quantity * price;
    } else if (
      field === "unitCost" ||
      field === "unitPrice" ||
      field === "price"
    ) {
      const price = parseFloat(value) || 0;
      item.costPrice = price;
      item.unitPrice = price;
      item.unitCost = price;
      item.total = item.quantity * price;
    }

    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const addEmptyProductRow = () => {
    const newItem: PurchaseOrderItem = {
      productId: "",
      productName: "",
      Product: null,
      ProductVariation: null,
      quantity: 1,
      unitCost: 0,
      unitPrice: 0,
      costPrice: 0,
      total: 0,
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const removeProductRow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
    setErrors((prev) => ({ ...prev, items: "" }));
  };

  const openNewOrderModal = () => {
    setFormData({
      supplierId: "",
      items: [],
      expectedDeliveryDate: "",
      notes: "",
      status: "draft",
      total: 0,
    });
    setIsEditing(false);
    setViewMode("edit");
    setShowModal(true);
    setErrors({ supplierId: "", items: "", expectedDeliveryDate: "" });
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors = {
      supplierId: "",
      items: "",
      expectedDeliveryDate: "",
    };

    if (!formData.supplierId) {
      newErrors.supplierId = t("Please select a supplier");
    }
    if (formData.items.length === 0) {
      newErrors.items = t("Please add at least one product");
    }
    if (!formData.expectedDeliveryDate) {
      newErrors.expectedDeliveryDate = t("Please select a delivery date");
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSave = async () => {
    if (!validateForm()) {
      err(t("Please fill in all required fields"));
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildOrderPayload();
      if (isEditing && formData.id) {
        const res = await BaseApi.put(
          `/purchase-orders/${formData.id}`,
          payload,
        );
        if (res.success) {
          success(t("Purchase order updated successfully"));
          fetchPurchaseOrders();
          fetchPOStats();
          closeModal();
        }
      } else {
        const res = await BaseApi.post("/purchase-orders", payload);
        if (res.success) {
          success(t("Purchase order created successfully"));
          fetchPurchaseOrders();
          fetchPOStats();
          closeModal();
        }
      }
    } catch (error) {
      console.error("Failed to save purchase order:", error);
      err(t("Failed to save purchase order"));
    } finally {
      setIsSaving(false);
    }
  };

  const buildOrderPayload = () => {
    return {
      supplierId: formData.supplierId,
      items: formData.items.map((item) => ({
        productId: item.variation?.id || item.productId,
        quantity: item.quantity,
        unitPrice: getItemPrice(item),
        unitCost: getItemPrice(item),
      })),
      expectedDeliveryDate: formData.expectedDeliveryDate,
      notes: formData.notes || "",
    };
  };

  const tryPurchaseOrderStatusRequests = async (
    requests: Array<() => Promise<any>>,
  ) => {
    let lastResponse = null;

    for (const request of requests) {
      const response = await request();
      if (response?.success) {
        return response;
      }
      lastResponse = response;
    }

    return lastResponse;
  };

  const handleApproveOrder = async (po: PurchaseOrder) => {
    openConfirmModal({
      title: t("Approve Purchase Order"),
      message: t("Are you sure you want to approve this purchase order?"),
      confirmText: t("Approve"),
      onConfirm: async () => {
        try {
          const res = await tryPurchaseOrderStatusRequests([
            () =>
              BaseApi.patch(`/purchase-orders/${po.id}/status`, {
                status: "approved",
              }),
            () =>
              BaseApi.patch(`/purchase-orders/${po.id}/approve`, {
                status: "approved",
              }),
          ]);

          if (res?.success) {
            success(t("Purchase order approved successfully"));
            fetchPurchaseOrders();
            fetchPOStats();
          } else {
            err(t("Failed to approve purchase order"));
          }
        } catch (error) {
          console.error("Error approving purchase order:", error);
          err(t("Failed to approve purchase order"));
        }
      },
    });
  };

  const handleMarkDelivered = async (po: PurchaseOrder) => {
    openConfirmModal({
      title: t("Mark Purchase Order as Delivered"),
      message: t(
        "Are you sure you want to mark this purchase order as delivered?",
      ),
      confirmText: t("Mark as Delivered"),
      onConfirm: async () => {
        try {
          const receivedDate = new Date().toISOString();
          const res = await tryPurchaseOrderStatusRequests([
            () =>
              BaseApi.patch(`/purchase-orders/${po.id}/status`, {
                status: "delivered",
                actualDeliveryDate: receivedDate,
              }),
            () =>
              BaseApi.post(`/purchase-orders/${po.id}/receive`, {
                receivedDate,
              }),
            () =>
              BaseApi.post(`/purchaseorders/${po.id}/receive`, {
                receivedDate,
              }),
          ]);

          if (res?.success) {
            success(t("Purchase order marked as delivered!"));
            fetchPurchaseOrders();
            fetchPOStats();
          } else {
            err(t("Failed to mark purchase order as delivered"));
          }
        } catch (error) {
          console.error("Error marking purchase order as delivered:", error);
          err(t("Failed to mark purchase order as delivered"));
        }
      },
    });
  };

  const openDeliverItemsModal = (po: PurchaseOrder) => {
    const receivedItems = po.items.map((item) => ({
      itemId: getPurchaseOrderItemId(item),
      productName: getProductDisplayName(item),
      variationName: getVariationDisplayName(item),
      orderedQuantity: Number(item.quantity || 0),
      quantityReceived: Number(item.quantity || 0),
    }));

    if (receivedItems.some((item) => !item.itemId)) {
      err(t("Some purchase order items are missing identifiers"));
      return;
    }

    setDeliverItemsOrder(po);
    setDeliverItemsData({
      receivedItems,
      notes: "",
    });
    setDeliverItemsModalOpen(true);
  };

  const closeDeliverItemsModal = () => {
    if (isReceivingItems) return;

    setDeliverItemsModalOpen(false);
    setDeliverItemsOrder(null);
    setDeliverItemsData({
      receivedItems: [],
      notes: "",
    });
  };

  const updateDeliveredItemQuantity = (index: number, value: string) => {
    const parsedValue = Number(value);

    setDeliverItemsData((prev) => ({
      ...prev,
      receivedItems: prev.receivedItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              quantityReceived:
                Number.isFinite(parsedValue) && parsedValue >= 0
                  ? parsedValue
                  : 0,
            }
          : item,
      ),
    }));
  };

  const handleDeliverItems = async () => {
    if (!deliverItemsOrder?.id) {
      err(t("Purchase order not found"));
      return;
    }

    if (deliverItemsData.receivedItems.length === 0) {
      err(t("No items available to receive"));
      return;
    }

    setIsReceivingItems(true);
    try {
      const payload = {
        receivedItems: deliverItemsData.receivedItems.map((item) => ({
          itemId: item.itemId,
          quantityReceived: Number(item.quantityReceived || 0),
        })),
        notes: deliverItemsData.notes.trim(),
      };

      const res = await tryPurchaseOrderStatusRequests([
        () =>
          BaseApi.post(
            `/purchase-orders/${deliverItemsOrder.id}/receive`,
            payload,
          ),
        () =>
          BaseApi.post(
            `/purchaseorders/${deliverItemsOrder.id}/receive`,
            payload,
          ),
      ]);

      if (res?.success) {
        success(t("Items delivered successfully"));
        closeDeliverItemsModal();
        fetchPurchaseOrders();
        fetchPOStats();
      } else {
        err(t("Failed to deliver items"));
      }
    } catch (error) {
      console.error("Error delivering purchase order items:", error);
      err(t("Failed to deliver items"));
    } finally {
      setIsReceivingItems(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return "--";
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "--";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      // hour: "2-digit",
      // minute: "2-digit",
    });
  };

  const getDownloadFileName = (
    order: PurchaseOrder,
    contentDisposition: string | null,
  ) => {
    const matchedFileName = contentDisposition?.match(
      /filename\*?=(?:UTF-8''|")?([^";]+)/i,
    )?.[1];

    if (matchedFileName) {
      return decodeURIComponent(matchedFileName.replace(/"/g, ""));
    }

    const orderReference = order.orderNumber || order.poNumber || order.id;
    return `purchase-order-${orderReference || "document"}.pdf`;
  };

  const handleDownloadPurchaseOrder = async (order: PurchaseOrder) => {
    if (!order.id || downloadingOrderId) {
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "");
    if (!baseUrl) {
      err(t("API base URL is not configured"));
      return;
    }

    const token = localStorage.getItem("authToken");
    const endpoint = `/purchase-orders/${order.id}/pdf`;

    setDownloadingOrderId(order.id);

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        getDownloadFileName(order, response.headers.get("content-disposition")),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      success(t("Purchase order downloaded successfully"));
    } catch (error) {
      console.error("Failed to download purchase order:", error);
      err(t("Failed to download purchase order"));
    } finally {
      setDownloadingOrderId(null);
    }
  };

  // POST /purchase-orders/{id}/cancel - Cancel purchase order
  const handleCancelOrder = async (po: PurchaseOrder) => {
    openConfirmModal({
      title: t("Cancel Purchase Order"),
      message: t("Are you sure you want to cancel this purchase order?"),
      confirmText: t("Delete"),
      onConfirm: async () => {
        try {
          const res = await BaseApi.post(`/purchase-orders/${po.id}/cancel`, {
            reason: "Cancelled by user",
          });
          if (res.success) {
            success(t("Purchase order cancelled!"));
            fetchPurchaseOrders();
            fetchPOStats();
          } else {
            err(t("Failed to cancel order"));
          }
        } catch (error) {
          console.error("Error cancelling order:", error);
          err(t("Failed to cancel order"));
        }
      },
    });
  };

  const handleEdit = async (po: PurchaseOrder) => {
    // Fetch fresh data before editing
    const freshData = await fetchPurchaseOrderById(po.id!);
    if (freshData) {
      setFormData(
        await hydratePurchaseOrderProducts(normalizePurchaseOrder(freshData)),
      );
    } else {
      setFormData(
        await hydratePurchaseOrderProducts(normalizePurchaseOrder(po)),
      );
    }
    setIsEditing(true);
    setViewMode("edit");
    setShowModal(true);
  };

  const handleView = async (po: PurchaseOrder) => {
    // Fetch fresh data before viewing
    const freshData = await fetchPurchaseOrderById(po.id!);
    if (freshData) {
      setFormData(
        await hydratePurchaseOrderProducts(normalizePurchaseOrder(freshData)),
      );
    } else {
      setFormData(
        await hydratePurchaseOrderProducts(normalizePurchaseOrder(po)),
      );
    }
    setIsEditing(false);
    setViewMode("view");
    setShowModal(true);
  };

  // DELETE /purchase-orders/{id} - Delete purchase order (soft delete)
  const handleDelete = async (po: PurchaseOrder) => {
    openConfirmModal({
      title: t("Delete Purchase Order"),
      message: t("Are you sure you want to delete this purchase order?"),
      confirmText: t("Delete"),
      onConfirm: async () => {
        setLoading(true);
        try {
          const res = await BaseApi.delete(`/purchase-orders/${po.id}`);
          if (res.success) {
            success(t("Purchase Order deleted!"));
            fetchPurchaseOrders();
            fetchPOStats();
          } else {
            err(t("Failed to delete purchase order"));
          }
        } catch (error) {
          console.error("Failed to delete purchase order:", error);
          err(t("Failed to delete purchase order"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  if (initialLoading) {
    return <LoadingComponent title={t("Purchase Orders")} />;
  }
  const openConfirmModal = (config: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }) => {
    setConfirmConfig({
      isOpen: true,
      title: config.title ?? t("Confirm Action"),
      message: config.message ?? t("Are you sure?"),
      confirmText: config.confirmText ?? "Confirm",
      cancelText: config.cancelText ?? "Cancel",
      onConfirm: () => {
        config.onConfirm();
        closeConfirmModal();
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmConfig((c) => ({ ...c, isOpen: false }));
  };

  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const currentPageData = purchaseOrders;

  const toDateInputValue = (isoDate: string) => {
    return isoDate ? isoDate.split("T")[0] : "";
  };

  return (
    <div className="min-h-screen bg-[#f2e6db]  p-4 sm:p-6 lg:p-8">
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div className="py-4">
          <div className="flex flex-col gap-6 bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-5 min-w-0">
              <div className="relative">
                <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                  <ShoppingCartCheckout className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl sm:text-4xl font-bold gradient-text mb-1">
                  {t("Purchase Orders")}
                </h1>
                <p className="text-gray-600 text-sm">
                  {t("Manage Your Purchase Orders")}
                </p>
              </div>
            </div>

            <Tooltip
              title={
                canAddEdit
                  ? t("Add Purchase Orders")
                  : t("You don't have permission to add purchase order")
              }
              placement="top"
            >
              <button
                disabled={!canAddEdit}
                onClick={openNewOrderModal}
                className="flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg font-semibold"
              >
                <Plus className="w-5 h-5" />
                {t("New Order")}
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Stats Section */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            <StatCard
              icon={ShoppingCartCheckout}
              title={t("Count")}
              value={finalTotalItems || stats.total}
              color="blue"
            />
            <StatCard
              icon={TrendingUp}
              title={t("Total")}
              value={Number(stats.totalValue)}
              color="green"
              isMoney={true}
            />
            <StatCard
              icon={Clock}
              title={t("Pending")}
              value={stats.pending}
              color="yellow"
            />
            <StatCard
              icon={AlertTriangle}
              title={t("Overdue")}
              value={stats.overdue}
              color="orange"
            />
            <StatCard
              icon={AlertTriangle}
              title={t("Cancelled")}
              value={stats.cancelled}
              color="red"
            />
            <StatCard
              icon={CheckCircle}
              title={t("Delivered")}
              value={stats.delivered}
              color="purple"
            />
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white justify-between rounded-3xl shadow-2xl p-4 sm:p-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search orders...")}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
            >
              <option value="all">{t("All Status")}</option>
              <option value="pending">{t("Pending")}</option>
              <option value="approved">{t("Approved")}</option>
              <option value="delivered">{t("Delivered")}</option>
              <option value="cancelled">{t("Cancelled")}</option>
            </select>
          </div>
          <div className="mt-3 sm:mt-4 flex items-center justify-between">
            <span className="text-xs sm:text-sm text-gray-600">
              {t("Showing")} {currentPageData.length} of {finalTotalItems}{" "}
              {t("orders")}
            </span>
            <Tooltip title={t("Refresh Purchase Orders")}>
              <button
                onClick={fetchPurchaseOrders}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {t("Refresh")}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-4">
        <div
          className={
            "overflow-x-auto" +
            (loading
              ? " opacity-50 pointer-events-none transition-opacity"
              : "")
          }
        >
          {currentPageData.length === 0 ? (
            <div className="text-center py-12 px-4 bg-white rounded-3xl">
              <ShoppingCartCheckout className="mx-auto w-12 h-12 sm:w-16 sm:h-11 text-gray-400" />
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                {t("No purchase orders found")}
              </p>
            </div>
          ) : (
            <table className="w-full table-auto">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Order #")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Supplier")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Date")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Expected Delivery")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Total")}
                  </th>
                  <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Status")}
                  </th>
                  <th className="px-4 py-4 w-[120px] sm:w-[160px] text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentPageData.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 font-semibold text-gray-900 break-words">
                      {order.orderNumber || order.poNumber || order.id}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-gray-700 break-words">
                      {getSupplierDisplayName(order)}
                    </td>
                    <td className="hidden sm:table-cell px-2 py-4 text-gray-700">
                      {formatDate(order?.createdAt || "")}
                    </td>
                    <td className="hidden sm:table-cell px-2 py-4 text-gray-700">
                      {formatDate(order.expectedDeliveryDate)}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 font-semibold text-gray-900">
                      <Price amount={Number(order.total)} />
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="px-3 py-4 w-[120px] sm:w-[160px] align-top">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {order.status === "pending" && (
                          <Tooltip title={t("Approve Purchase Order")}>
                            <button
                              onClick={() => handleApproveOrder(order)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title={t("Approve")}
                            >
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </Tooltip>
                        )}
                        {order.status === "approved" && (
                          <Tooltip title={t("Deliver Items")}>
                            <button
                              onClick={() => openDeliverItemsModal(order)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title={t("Deliver Items")}
                            >
                              <Truck className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </Tooltip>
                        )}
                        {order.status === "approved" && (
                          <Tooltip
                            title={t("Mark Purchase Order as Delivered")}
                          >
                            <button
                              onClick={() => handleMarkDelivered(order)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title={t("Mark as Delivered")}
                            >
                              <ShoppingCartCheckout className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </Tooltip>
                        )}
                        {order.status !== "cancelled" &&
                          order.status !== "delivered" && (
                            <Tooltip title={t("Cancel Purchase Orders")}>
                              <button
                                onClick={() => handleCancelOrder(order)}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                title={t("Cancel Order")}
                              >
                                <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            </Tooltip>
                          )}
                        <Tooltip title={t("View Purchase Orders")}>
                          <button
                            onClick={() => handleView(order)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={t("View")}
                          >
                            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </Tooltip>
                        <Tooltip title={t("Download Purchase Order")}>
                          <button
                            onClick={() => handleDownloadPurchaseOrder(order)}
                            disabled={downloadingOrderId === order.id}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t("Download")}
                          >
                            {downloadingOrderId === order.id ? (
                              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                            )}
                          </button>
                        </Tooltip>
                        {order.status !== "delivered" && (
                          <Tooltip
                            title={
                              canEdit
                                ? t("Edit Purchase Orders")
                                : t(
                                    "You don't have permission to edit purchase order",
                                  )
                            }
                            placement="top"
                          >
                            <button
                              disabled={!canEdit}
                              onClick={() => handleEdit(order)}
                              className="p-2 text-blue-600 hover:bg-[#0d223f] rounded-lg transition-colors"
                              title={t("Edit")}
                            >
                              <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </Tooltip>
                        )}
                        {order.status !== "delivered" && (
                          <Tooltip
                            title={
                              canDelete
                                ? t("Delete Purchase Orders")
                                : t(
                                    "You don't have permission to delete purchase order",
                                  )
                            }
                            placement="top"
                          >
                            <button
                              disabled={!canDelete}
                              onClick={() => handleDelete(order)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title={t("Delete")}
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </Tooltip>
                        )}
                        {/* <button className="p-2 text-blue-600 hover:bg-[#0d223f] rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {finalTotalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={finalTotalPages}
            totalItems={finalTotalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            showPageSizeSelector={true}
            isLoading={loading}
            compact={true}
          />
        )}
      </div>

      {/* Main Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-black px-6 py-5 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">
                  {viewMode === "view"
                    ? t("Purchase Order Details")
                    : isEditing
                      ? t("Edit Purchase Order")
                      : t("New Purchase Order")}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Supplier Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Supplier")} <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.supplierId}
                  onChange={(e) =>
                    handleInputChange("supplierId", e.target.value)
                  }
                  className={`w-full px-4 py-3 border-2 ${
                    errors.supplierId ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:border-black focus:outline-none transition-colors`}
                  disabled={viewMode === "view"}
                >
                  <option value="">{t("Select supplier")}</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.firstName?.trim() || supplier.lastName?.trim()
                        ? `${supplier.firstName ?? ""} ${
                            supplier.lastName ?? ""
                          }`.trim()
                        : supplier.name || supplier.phone || supplier.email}
                    </option>
                  ))}
                </select>
                {errors.supplierId && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.supplierId}
                  </p>
                )}
              </div>

              {/* Expected Delivery Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Expected Delivery Date")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDateInputValue(formData.expectedDeliveryDate)}
                  onChange={(e) =>
                    handleInputChange("expectedDeliveryDate", e.target.value)
                  }
                  className={`w-full px-4 py-3 border-2 ${
                    errors.expectedDeliveryDate
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded-lg focus:border-black focus:outline-none transition-colors`}
                  disabled={viewMode === "view"}
                />
                {errors.expectedDeliveryDate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.expectedDeliveryDate}
                  </p>
                )}
              </div>

              {/* Products Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-semibold text-gray-700">
                    {t("Products")} <span className="text-red-500">*</span>
                  </label>
                  {viewMode !== "view" && (
                    <button
                      onClick={addEmptyProductRow}
                      className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      {t("Add Product")}
                    </button>
                  )}
                </div>

                {errors.items && (
                  <p className="mb-2 text-sm text-red-500">{errors.items}</p>
                )}

                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                            {t("Product")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                            {t("Variation")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                            {t("Quantity")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                            {t("Cost Price")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                            {t("Total")}
                          </th>
                          {viewMode !== "view" && (
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">
                              {t("Action")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {formData.items.length === 0 ? (
                          <tr>
                            <td
                              colSpan={viewMode === "view" ? 5 : 6}
                              className="px-4 py-8 text-center text-gray-500 text-sm"
                            >
                              {t("No products added yet")}
                            </td>
                          </tr>
                        ) : (
                          formData.items.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-2 py-3">
                                {viewMode === "view" ? (
                                  <span className="text-gray-800">
                                    {getProductDisplayName(item)}
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setProductSearchModalRowIndex(index);
                                      setProductSearchModalOpen(true);
                                      setProductSearchQuery("");
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none text-sm text-left bg-white hover:bg-gray-50 flex items-center justify-between"
                                  >
                                    <span className="truncate">
                                      {item.Product?.id
                                        ? (() => {
                                            const productName =
                                              getProductDisplayName(item);
                                            return item.Product?.sku
                                              ? `${productName} (${item.Product.sku})`
                                              : productName;
                                          })()
                                        : t("Select product")}
                                    </span>
                                    <Search className="w-4 h-4 text-gray-400" />
                                  </button>
                                )}
                              </td>
                              <td className="px-2 py-3">
                                {viewMode === "view" ? (
                                  <span className="text-gray-800">
                                    {getVariationDisplayName(item)}
                                  </span>
                                ) : loadingProductId === item.Product?.id ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
                                    <span className="text-gray-500 text-sm">
                                      {t("Loading...")}
                                    </span>
                                  </div>
                                ) : item.Product?.hasVariations &&
                                  item.Product?.variations &&
                                  item.Product.variations.length > 0 ? (
                                  <select
                                    value={item.variation?.id || ""}
                                    onChange={(e) =>
                                      updateProductRow(
                                        index,
                                        "variationId",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none text-sm"
                                  >
                                    <option value="">
                                      {t("Select variation")}
                                    </option>
                                    {item.Product.variations.map(
                                      (variation) => (
                                        <option
                                          key={variation.id}
                                          value={variation.id}
                                        >
                                          {Object.values(
                                            variation.attributes,
                                          ).join(", ")}{" "}
                                          - {variation.sku}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                ) : (
                                  <span className="text-gray-500 text-sm">
                                    {t("No variations")}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-3">
                                {viewMode === "view" ? (
                                  <span className="text-gray-800">
                                    {item.quantity}
                                  </span>
                                ) : (
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) =>
                                      updateProductRow(
                                        index,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                    min="1"
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none text-sm"
                                  />
                                )}
                              </td>
                              <td className="px-2 py-3">
                                {viewMode === "view" ? (
                                  <Price amount={getItemPrice(item)} />
                                ) : (
                                  <input
                                    type="number"
                                    value={getItemPrice(item)}
                                    onChange={(e) =>
                                      updateProductRow(
                                        index,
                                        "costPrice",
                                        e.target.value,
                                      )
                                    }
                                    min="0"
                                    step="0.01"
                                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none text-sm"
                                  />
                                )}
                              </td>
                              <td className="px-2 py-3">
                                <Price amount={Number(item.total)} />
                              </td>
                              {viewMode !== "view" && (
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => removeProductRow(index)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Notes")}
                </label>
                <textarea
                  value={formData.notes || ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none resize-none"
                  placeholder={t("Add any additional notes...")}
                  disabled={viewMode === "view"}
                />
              </div>

              {/* Total Amount */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-700">
                    {t("Order Total")}:
                  </span>
                  <span className="text-2xl font-bold text-black">
                    <Price amount={formData.total || 0} />
                  </span>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t-2 border-gray-200">
                <button
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-semibold"
                >
                  {viewMode === "view" ? t("Close") : t("Cancel")}
                </button>

                {viewMode === "view" &&
                  formData.id &&
                  (formData.status === "approved" ||
                    formData.status === "delivered") &&
                  (() => {
                    // Only stock that was actually received can go back
                    const hasReceivedStock = formData.items.some(
                      (item) => Number(item.stockAppliedQuantity || 0) > 0,
                    );
                    const enabled = canCreateReturn && hasReceivedStock;
                    return (
                      <Tooltip
                        title={
                          !canCreateReturn
                            ? t(
                                "You don't have permission to create purchase returns",
                              )
                            : !hasReceivedStock
                              ? t("No received stock to return yet")
                              : t("Return to Supplier")
                        }
                        placement="top"
                      >
                        <span className="flex-1 flex">
                          <button
                            disabled={!enabled}
                            onClick={() =>
                              navigate(
                                `/admin/purchase-returns?purchaseOrderId=${formData.id}`,
                              )
                            }
                            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                          >
                            <PackageMinus className="w-5 h-5" />
                            <span>{t("Return to Supplier")}</span>
                          </button>
                        </span>
                      </Tooltip>
                    );
                  })()}

                {viewMode !== "view" && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t("Saving...")}</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        <span>
                          {isEditing ? t("Update Order") : t("Create Order")}
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Search Modal */}
      {productSearchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-black"
              onClick={() => setProductSearchModalOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold mb-4">{t("Select Product")}</h2>
            <input
              type="text"
              placeholder={t("Search by name or SKU...")}
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:border-black focus:outline-none"
              autoFocus
            />
            <div className="max-h-72 overflow-y-auto">
              {getFilteredProducts(productSearchQuery).length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {t("No products found")}
                </div>
              ) : (
                getFilteredProducts(productSearchQuery).map((product) => {
                  const productName =
                    typeof product.name === "string"
                      ? product.name
                      : product.name?.en || "Unknown Product";
                  return (
                    <button
                      key={product.id}
                      onClick={() => {
                        if (productSearchModalRowIndex !== null) {
                          updateProductRow(
                            productSearchModalRowIndex,
                            "productId",
                            product.id,
                          );
                        }
                        setProductSearchModalOpen(false);
                        setProductSearchModalRowIndex(null);
                        setProductSearchQuery("");
                      }}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-gray-900">
                        {productName}
                      </div>
                      <div className="text-xs text-gray-500">
                        SKU: {product.sku}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {deliverItemsModalOpen && deliverItemsOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-black px-6 py-5 border-b border-gray-200 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {t("Deliver Items")}
                  </h2>
                  <p className="text-sm text-gray-200 mt-1">
                    {deliverItemsOrder.orderNumber ||
                      deliverItemsOrder.poNumber ||
                      deliverItemsOrder.id}
                  </p>
                </div>
                <button
                  onClick={closeDeliverItemsModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isReceivingItems}
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-4">
                {deliverItemsData.receivedItems.map((item, index) => (
                  <div
                    key={`${item.itemId}-${index}`}
                    className="border border-gray-200 rounded-xl p-4"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">
                          {item.productName}
                        </h3>
                        {item.variationName &&
                          item.variationName !== t("No variations") && (
                            <p className="text-sm text-gray-500 mt-1">
                              {item.variationName}
                            </p>
                          )}
                        <p className="text-sm text-gray-600 mt-1">
                          {t("Ordered Quantity")}: {item.orderedQuantity}
                        </p>
                      </div>

                      <div className="w-full md:w-48">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t("Quantity Delivered")}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={item.quantityReceived}
                          onChange={(e) =>
                            updateDeliveredItemQuantity(index, e.target.value)
                          }
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Notes")}
                </label>
                <textarea
                  value={deliverItemsData.notes}
                  onChange={(e) =>
                    setDeliverItemsData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={4}
                  placeholder={t("Add delivery notes")}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none transition-colors resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={closeDeliverItemsModal}
                  className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  disabled={isReceivingItems}
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleDeliverItems}
                  disabled={isReceivingItems}
                  className="px-6 py-3 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isReceivingItems ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4" />
                      {t("Deliver Items")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={() =>
          setConfirmConfig((prev) => ({ ...prev, isOpen: false }))
        }
      />
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  value: number;
  color: "blue" | "green" | "orange" | "red" | "purple" | "yellow";
  isMoney?: boolean;
}

const StatCard = ({
  icon: Icon,
  title,
  value = 0,
  color,
  isMoney = false,
}: StatCardProps) => {
  const colorClasses = {
    blue: "bg-[#0d223f]",
    green: "bg-green-500",
    orange: "bg-orange-500",
    red: "bg-red-500",
    purple: "bg-purple-500",
    yellow: "bg-yellow-500",
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-2 hover:shadow-xl transition-shadow duration-300 w-full">
      <div className="flex items-start gap-2">
        
        {/* Icon */}
        <div className={`p-2 sm:p-3 ${colorClasses[color]} rounded-lg flex-shrink-0`}>
          <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
        </div>

        {/* Text */}
        <div className="flex-1 break-words">
          <p className="text-xs sm:text-sm text-gray-700 font-medium">
            {title}
          </p>

          <p className="font-bold text-gray-700 
                        text-sm sm:text-lg md:text-xl 
                        break-words leading-tight">
            {isMoney ? <Price amount={value} /> : value}
          </p>
        </div>

      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();

  const statusConfig: Record<
    string,
    { color: string; bg: string; label: string }
  > = {
    draft: { color: "text-gray-700", bg: "bg-gray-100", label: t("Draft") },
    pending: {
      color: "text-orange-700",
      bg: "bg-orange-100",
      label: t("Pending"),
    },
    approved: {
      color: "text-white",
      bg: "bg-[#0d223f]",
      label: t("Approved"),
    },
    delivered: {
      color: "text-green-700",
      bg: "bg-green-100",
      label: t("Delivered"),
    },
    cancelled: {
      color: "text-red-700",
      bg: "bg-red-100",
      label: t("Cancelled"),
    },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.color} ${config.bg}`}
    >
      {config.label}
    </span>
  );
};

export default PurchaseOrderManagementPage;
